import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MarkdownRenderer from "./markdown-renderer";

/**
 * Deep-analysis multi-graph responses arrive as a single markdown string fenced
 * with per-tab markers, e.g.:
 *
 *   <<<<<TAB:SUMMARY>>>>>
 *   ...markdown...
 *   <<<<<TAB:FUNCTIONAL>>>>>
 *   ...markdown...
 *
 * Content for a tab runs until the next marker (or end of string). Closing
 * markers (<<<<</TAB:KEY>>>>>) are tolerated/stripped if present. When one or
 * more markers are detected the message renders as tabs; otherwise the caller
 * falls back to plain markdown.
 */

export interface TabSection {
  key: string;
  label: string;
  content: string;
}

// Canonical order + display labels. Only sections actually present (with
// non-empty content) are rendered, in this order.
const TAB_DEFS: { key: string; label: string }[] = [
  { key: "summary", label: "Summary" },
  { key: "functional", label: "Functional" },
  { key: "design", label: "Design" },
  { key: "code", label: "Code" },
  { key: "architecture", label: "Architecture" },
];

const OPEN_MARKER = /<<<<<TAB:(SUMMARY|FUNCTIONAL|DESIGN|CODE|ARCHITECTURE)>>>>>/gi;
const ANY_MARKER = /<<<<<\/?TAB:[A-Z]+>>>>>/gi;

export function parseTabbedContent(text: string | undefined | null): TabSection[] {
  if (!text) return [];

  const matches = [...text.matchAll(OPEN_MARKER)];
  if (matches.length === 0) return [];

  const byKey: Record<string, string> = {};
  for (let i = 0; i < matches.length; i++) {
    const key = matches[i][1].toLowerCase();
    const start = (matches[i].index ?? 0) + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index ?? text.length : text.length;
    const content = text.slice(start, end).replace(ANY_MARKER, "").trim();
    if (content) byKey[key] = content; // last occurrence wins
  }

  return TAB_DEFS.filter((t) => byKey[t.key]).map((t) => ({
    key: t.key,
    label: t.label,
    content: byKey[t.key],
  }));
}

export default function TabbedMessageRenderer({ text }: { text: string }) {
  const sections = parseTabbedContent(text);

  // No markers -> let the normal markdown path handle it.
  if (sections.length === 0) {
    return <MarkdownRenderer content={text} />;
  }

  return (
    <Tabs defaultValue={sections[0].key} className="w-full">
      <TabsList className="flex flex-wrap h-auto gap-1 bg-surface-input">
        {sections.map((s) => (
          <TabsTrigger key={s.key} value={s.key}>
            {s.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {sections.map((s) => (
        <TabsContent key={s.key} value={s.key}>
          <MarkdownRenderer content={s.content} />
        </TabsContent>
      ))}
    </Tabs>
  );
}
